import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getLevelFromXP } from '@/data/levels';
import { API_CONFIG } from '@/config';
import {
  createDefaultProjects,
  getProjectIdForVertical,
  mergeProjectsWithDefaults,
  type ProjectCompareRun,
  type OutcomeLog,
  type OutcomeMetric,
  type ProfileProject,
  type ProfileVertical,
  type ProjectProfileAudit,
  type ProjectModuleId,
  type ProjectProfileSectionId,
  type ProjectModuleStatus,
  type ProfileRewriteResult,
  type ProfileSectionAnalysis,
  type RetestLog,
} from '@photofeeler/shared';
import type { OutfitReview } from '@photofeeler/shared';

interface WeeklyResult {
  week: string;
  value: number;
}

interface AnalysisRecordInput {
  vertical: ProfileVertical;
  photoId: string;
  score: number;
  recordedAt?: string;
}

interface ProjectRecommendationInput {
  profileSuggestions?: string[];
  polishChecklist?: string[];
  outfitReview?: OutfitReview;
}

interface GameState {
  // Contexts the user has activated
  activeContexts: string[];
  primaryContext: string;

  // XP and progression
  xp: number;
  level: number;

  // Streak
  streakDays: number;
  lastStreakDate: string | null;

  // Achievements unlocked (array of achievement IDs)
  achievements: string[];

  // Onboarding complete
  onboardingComplete: boolean;

  // Photo School progress
  completedLessons: string[];

  // Weekly result tracking
  weeklyResults: WeeklyResult[];

  // Project workspaces
  projects: ProfileProject[];
  activeProjectId: string;
  outcomeLogs: OutcomeLog[];
  retestLogs: RetestLog[];

  // Sync status
  _syncing: boolean;
  _lastSyncedAt: string | null;

  // Actions
  addXP: (amount: number) => void;
  setContexts: (contexts: string[], primary: string) => void;
  completeLesson: (lessonId: string) => void;
  updateStreak: () => void;
  unlockAchievement: (id: string) => void;
  completeOnboarding: () => void;
  logWeeklyResult: (value: number) => void;
  setActiveProject: (projectId: string) => void;
  completeProjectModule: (projectId: string, moduleId: ProjectModuleId) => void;
  updateProjectProfileSection: (
    projectId: string,
    sectionId: ProjectProfileSectionId,
    value: string
  ) => void;
  saveProjectProfileVersion: (
    projectId: string,
    sectionId: ProjectProfileSectionId,
    label?: string
  ) => void;
  updateProjectOutfitNotes: (projectId: string, notes: string) => void;
  applyProjectRecommendations: (projectId: string, input: ProjectRecommendationInput) => void;
  applyProjectRewrite: (
    projectId: string,
    sectionId: ProjectProfileSectionId,
    rewrite: ProfileRewriteResult
  ) => void;
  applyProjectProfileAnalysis: (
    projectId: string,
    sectionId: ProjectProfileSectionId,
    analysis: ProfileSectionAnalysis
  ) => void;
  applyProjectAudit: (projectId: string, audit: ProjectProfileAudit) => void;
  restoreProjectProfileVersion: (
    projectId: string,
    sectionId: ProjectProfileSectionId,
    versionId: string
  ) => void;
  recordPhotoAnalysis: (input: AnalysisRecordInput) => void;
  recordRetest: (input: AnalysisRecordInput) => void;
  recordCompareRun: (projectId: string, compareRun: ProjectCompareRun) => void;
  logOutcome: (projectId: string, metric: OutcomeMetric, value: number, note?: string) => void;

  // Server sync
  loadFromServer: (token: string) => Promise<void>;
  saveToServer: (token: string) => Promise<void>;
  resetState: () => void;
}

/** Return today's date as an ISO date string (YYYY-MM-DD) in local time. */
function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Return yesterday's date as an ISO date string (YYYY-MM-DD) in local time. */
function yesterdayISO(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Return the current ISO week string (YYYY-Www). */
function currentWeek(): string {
  const now = new Date();
  const jan1 = new Date(now.getFullYear(), 0, 1);
  const days = Math.floor(
    (now.getTime() - jan1.getTime()) / (24 * 60 * 60 * 1000)
  );
  const week = Math.ceil((days + jan1.getDay() + 1) / 7);
  return `${now.getFullYear()}-W${String(week).padStart(2, '0')}`;
}

function projectPhotoTarget(vertical: ProfileVertical): number {
  return vertical === 'dating' ? 4 : 2;
}

function requiredProfileSectionIds(vertical: ProfileVertical): ProjectProfileSectionId[] {
  return vertical === 'dating'
    ? ['bio', 'prompt-1', 'prompt-2']
    : ['headline', 'about'];
}

function isFilled(value: string | null | undefined): boolean {
  return Boolean(value && value.trim().length > 0);
}

function appendDraftVersion(
  draftVersions: ProfileProject['profileWorkspace']['sections'][number]['draftVersions'],
  version: ProfileProject['profileWorkspace']['sections'][number]['draftVersions'][number]
) {
  const existing = draftVersions.filter(item => item.id !== version.id);
  const deduped = existing.filter(item => !(item.content === version.content && item.label === version.label));
  return [version, ...deduped].slice(0, 8);
}

function syncProjectState(
  project: ProfileProject,
  outcomeLogs: OutcomeLog[],
  retestLogs: RetestLog[]
): ProfileProject {
  const photoTarget = projectPhotoTarget(project.vertical);
  const hasPhotos = project.photoIds.length > 0;
  const hasEnoughPhotos = project.photoIds.length >= photoTarget;
  const latestCompare = project.compareRuns.at(-1);
  const hasLeadPhoto = !!(project.bestPhotoId || latestCompare?.winnerPhotoId);
  const hasOutcomeLog = outcomeLogs.some(log => log.projectId === project.id);
  const hasRetestLog = retestLogs.some(log => log.projectId === project.id);
  const requiredSections = requiredProfileSectionIds(project.vertical);
  const filledRequiredSections = requiredSections.filter(sectionId =>
    isFilled(project.profileWorkspace.sections.find(section => section.id === sectionId)?.value)
  ).length;
  const hasProfileSuggestions =
    project.profileWorkspace.suggestions.length > 0 || project.profileWorkspace.polishChecklist.length > 0;
  const hasProfileAnalysis = project.profileWorkspace.sections.some(section => !!section.analysis);
  const hasProfileAudit = !!project.profileWorkspace.audit;
  const hasOutfitNotes = isFilled(project.outfitWorkspace.notes);
  const hasOutfitReview = !!project.outfitWorkspace.review;

  const modules = project.modules.map(module => {
    if (module.status === 'completed') {
      return { ...module, completion: 100 };
    }

    if (module.id === 'photos') {
      const completion = hasPhotos
        ? Math.min(100, Math.round((project.photoIds.length / photoTarget) * 100))
        : 0;
      const status: ProjectModuleStatus = !hasPhotos
        ? 'not_started'
        : hasEnoughPhotos && hasLeadPhoto
          ? 'completed'
          : 'in_progress';
      return {
        ...module,
        completion,
        status,
      };
    }

    if (module.id === 'profile') {
      const completion = filledRequiredSections > 0
        ? Math.round((filledRequiredSections / requiredSections.length) * 100)
        : hasProfileSuggestions || hasProfileAnalysis || hasProfileAudit
          ? 35
          : 0;
      const status: ProjectModuleStatus = filledRequiredSections === requiredSections.length
        ? 'completed'
        : filledRequiredSections > 0 || hasProfileSuggestions || hasProfileAnalysis || hasProfileAudit
          ? 'in_progress'
          : 'not_started';
      return {
        ...module,
        completion,
        status,
      };
    }

    if (module.id === 'outfit') {
      const completion = hasOutfitReview ? 100 : hasOutfitNotes ? 45 : 0;
      const status: ProjectModuleStatus = hasOutfitReview
        ? 'completed'
        : hasOutfitNotes
          ? 'in_progress'
          : 'not_started';
      return {
        ...module,
        completion,
        status,
      };
    }

    if (module.id === 'results') {
      const completion = hasOutcomeLog ? 100 : hasRetestLog ? 60 : 0;
      const status: ProjectModuleStatus = hasOutcomeLog
        ? 'completed'
        : hasRetestLog
          ? 'in_progress'
          : 'not_started';
      return {
        ...module,
        completion,
        status,
      };
    }

    return module;
  });

  const nextActions = project.nextActions.map(action => {
    if (action.id === 'upload-lineup') {
      return { ...action, completed: hasEnoughPhotos };
    }
    if (action.id === 'pick-lead') {
      return { ...action, completed: hasLeadPhoto };
    }
    if (action.id === 'tighten-profile') {
      return { ...action, completed: filledRequiredSections === requiredSections.length };
    }
    if (action.id === 'outfit-check') {
      return { ...action, completed: hasOutfitReview };
    }
    if (action.id === 'log-results') {
      return { ...action, completed: hasOutcomeLog };
    }
    return action;
  });

  const status = hasOutcomeLog
    ? 'tracking'
    : hasRetestLog
      ? 'improving'
      : hasPhotos
        ? 'active'
        : 'setup';

  return {
    ...project,
    status,
    modules,
    nextActions,
  };
}

function updateProjectCollection(
  projects: ProfileProject[],
  projectId: string,
  updater: (project: ProfileProject) => ProfileProject,
  outcomeLogs: OutcomeLog[],
  retestLogs: RetestLog[]
): ProfileProject[] {
  const merged = mergeProjectsWithDefaults(projects);
  return merged.map(project =>
    project.id === projectId ? syncProjectState(updater(project), outcomeLogs, retestLogs) : syncProjectState(project, outcomeLogs, retestLogs)
  );
}

const DEFAULT_STATE = {
  activeContexts: [] as string[],
  primaryContext: '',
  xp: 0,
  level: 1,
  streakDays: 0,
  lastStreakDate: null as string | null,
  achievements: [] as string[],
  onboardingComplete: false,
  completedLessons: [] as string[],
  weeklyResults: [] as WeeklyResult[],
  projects: createDefaultProjects(),
  activeProjectId: getProjectIdForVertical('dating'),
  outcomeLogs: [] as OutcomeLog[],
  retestLogs: [] as RetestLog[],
  _syncing: false,
  _lastSyncedAt: null as string | null,
};

// Debounce save to server — avoids hammering API on rapid state changes
let saveTimeout: ReturnType<typeof setTimeout> | null = null;
function debouncedSave(token: string) {
  if (saveTimeout) clearTimeout(saveTimeout);
  saveTimeout = setTimeout(() => {
    useGameStore.getState().saveToServer(token);
  }, 1000);
}

// Immediate save for critical state changes (analysis results, retests, etc.)
// Clears any pending debounce and saves right away to prevent data loss on navigation.
function saveImmediately(token: string) {
  if (saveTimeout) clearTimeout(saveTimeout);
  useGameStore.getState().saveToServer(token);
}

// Get auth token from Supabase session in localStorage
function getAuthToken(): string | null {
  try {
    // Supabase stores session under a key like sb-<project-ref>-auth-token
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
        const data = JSON.parse(localStorage.getItem(key) || '{}');
        return data?.access_token || null;
      }
    }
  } catch { /* ignore */ }
  return null;
}

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      ...DEFAULT_STATE,

      addXP: (amount: number) => {
        set(state => {
          const newXP = state.xp + amount;
          return { xp: newXP, level: getLevelFromXP(newXP) };
        });
        const token = getAuthToken();
        if (token) debouncedSave(token);
      },

      setContexts: (contexts: string[], primary: string) => {
        set({ activeContexts: contexts, primaryContext: primary });
        const token = getAuthToken();
        if (token) debouncedSave(token);
      },

      completeLesson: (lessonId: string) => {
        set(state => {
          if (state.completedLessons.includes(lessonId)) return state;
          const newCompleted = [...state.completedLessons, lessonId];
          const newXP = state.xp + 25;
          return {
            completedLessons: newCompleted,
            xp: newXP,
            level: getLevelFromXP(newXP),
          };
        });
        const token = getAuthToken();
        if (token) debouncedSave(token);
      },

      updateStreak: () => {
        set(state => {
          const today = todayISO();
          const yesterday = yesterdayISO();

          if (state.lastStreakDate === today) return state;
          if (state.lastStreakDate === yesterday) {
            return { streakDays: state.streakDays + 1, lastStreakDate: today };
          }
          return { streakDays: 1, lastStreakDate: today };
        });
        const token = getAuthToken();
        if (token) debouncedSave(token);
      },

      unlockAchievement: (id: string) => {
        set(state => {
          if (state.achievements.includes(id)) return state;
          const newAchievements = [...state.achievements, id];
          const newXP = state.xp + 50;
          return {
            achievements: newAchievements,
            xp: newXP,
            level: getLevelFromXP(newXP),
          };
        });
        const token = getAuthToken();
        if (token) debouncedSave(token);
      },

      completeOnboarding: () => {
        const state = get();
        const newXP = state.xp + 10;
        set({
          onboardingComplete: true,
          xp: newXP,
          level: getLevelFromXP(newXP),
        });
        const token = getAuthToken();
        if (token) debouncedSave(token);
      },

      logWeeklyResult: (value: number) => {
        set(state => {
          const week = currentWeek();
          const existing = state.weeklyResults.find(r => r.week === week);
          if (existing) {
            return {
              weeklyResults: state.weeklyResults.map(r =>
                r.week === week ? { ...r, value } : r
              ),
            };
          }
          return {
            weeklyResults: [...state.weeklyResults, { week, value }],
          };
        });
        const token = getAuthToken();
        if (token) debouncedSave(token);
      },

      setActiveProject: (projectId: string) => {
        set({ activeProjectId: projectId, projects: mergeProjectsWithDefaults(get().projects) });
        const token = getAuthToken();
        if (token) debouncedSave(token);
      },

      completeProjectModule: (projectId: string, moduleId: ProjectModuleId) => {
        const nextProjects = updateProjectCollection(
          get().projects,
          projectId,
          project => ({
            ...project,
            updatedAt: new Date().toISOString(),
            modules: project.modules.map(module =>
              module.id === moduleId
                ? { ...module, status: 'completed', completion: 100 }
                : module
            ),
          }),
          get().outcomeLogs,
          get().retestLogs
        );

        set({ projects: nextProjects });
        const token = getAuthToken();
        if (token) debouncedSave(token);
      },

      updateProjectProfileSection: (projectId, sectionId, value) => {
        const timestamp = new Date().toISOString();
        const nextProjects = updateProjectCollection(
          get().projects,
          projectId,
          project => ({
            ...project,
            profileWorkspace: {
              ...project.profileWorkspace,
              sections: project.profileWorkspace.sections.map(section =>
                section.id === sectionId ? { ...section, value, analysis: null } : section
              ),
              audit: null,
              updatedAt: timestamp,
            },
            updatedAt: timestamp,
          }),
          get().outcomeLogs,
          get().retestLogs
        );

        set({ projects: nextProjects });
        const token = getAuthToken();
        if (token) debouncedSave(token);
      },

      saveProjectProfileVersion: (projectId, sectionId, label = 'Manual draft') => {
        const timestamp = new Date().toISOString();
        const nextProjects = updateProjectCollection(
          get().projects,
          projectId,
          project => ({
            ...project,
            profileWorkspace: {
              ...project.profileWorkspace,
              sections: project.profileWorkspace.sections.map(section => {
                if (section.id !== sectionId || !isFilled(section.value)) return section;

                return {
                  ...section,
                  draftVersions: appendDraftVersion(section.draftVersions, {
                    id: `${projectId}-${sectionId}-${timestamp}`,
                    content: section.value,
                    source: 'manual',
                    label,
                    tone: null,
                    rationale: null,
                    highlights: [],
                    createdAt: timestamp,
                  }),
                };
              }),
              updatedAt: timestamp,
            },
            updatedAt: timestamp,
          }),
          get().outcomeLogs,
          get().retestLogs
        );

        set({ projects: nextProjects });
        const token = getAuthToken();
        if (token) debouncedSave(token);
      },

      updateProjectOutfitNotes: (projectId, notes) => {
        const timestamp = new Date().toISOString();
        const nextProjects = updateProjectCollection(
          get().projects,
          projectId,
          project => ({
            ...project,
            profileWorkspace: {
              ...project.profileWorkspace,
              audit: null,
            },
            outfitWorkspace: {
              ...project.outfitWorkspace,
              notes,
              updatedAt: timestamp,
            },
            updatedAt: timestamp,
          }),
          get().outcomeLogs,
          get().retestLogs
        );

        set({ projects: nextProjects });
        const token = getAuthToken();
        if (token) debouncedSave(token);
      },

      applyProjectRecommendations: (projectId, input) => {
        const timestamp = new Date().toISOString();
        const nextProjects = updateProjectCollection(
          get().projects,
          projectId,
          project => ({
            ...project,
            profileWorkspace: {
              ...project.profileWorkspace,
              suggestions: input.profileSuggestions?.length
                ? Array.from(new Set(input.profileSuggestions))
                : project.profileWorkspace.suggestions,
              polishChecklist: input.polishChecklist?.length
                ? Array.from(
                    new Set([
                      ...project.profileWorkspace.polishChecklist,
                      ...input.polishChecklist,
                    ])
                  )
                : project.profileWorkspace.polishChecklist,
              audit:
                input.profileSuggestions?.length || input.polishChecklist?.length || input.outfitReview
                  ? null
                  : project.profileWorkspace.audit,
              updatedAt:
                input.profileSuggestions?.length || input.polishChecklist?.length || input.outfitReview
                  ? timestamp
                  : project.profileWorkspace.updatedAt,
            },
            outfitWorkspace: {
              ...project.outfitWorkspace,
              review: input.outfitReview || project.outfitWorkspace.review,
              updatedAt: input.outfitReview ? timestamp : project.outfitWorkspace.updatedAt,
            },
            updatedAt: timestamp,
          }),
          get().outcomeLogs,
          get().retestLogs
        );

        set({ projects: nextProjects });
        const token = getAuthToken();
        if (token) saveImmediately(token);
      },

      applyProjectRewrite: (projectId, sectionId, rewrite) => {
        const timestamp = new Date().toISOString();
        const nextProjects = updateProjectCollection(
          get().projects,
          projectId,
          project => ({
            ...project,
            profileWorkspace: {
              ...project.profileWorkspace,
              sections: project.profileWorkspace.sections.map(section => {
                if (section.id !== sectionId) return section;

                let draftVersions = section.draftVersions;
                if (isFilled(section.value) && section.value !== rewrite.content) {
                  draftVersions = appendDraftVersion(draftVersions, {
                    id: `${projectId}-${sectionId}-manual-${timestamp}`,
                    content: section.value,
                    source: 'manual',
                    label: 'Manual draft',
                    tone: null,
                    rationale: null,
                    highlights: [],
                    createdAt: timestamp,
                  });
                }

                draftVersions = appendDraftVersion(draftVersions, {
                  id: `${projectId}-${sectionId}-rewrite-${timestamp}`,
                  content: rewrite.content,
                  source: 'ai_rewrite',
                  label: `AI rewrite · ${rewrite.tone}`,
                  tone: rewrite.tone,
                  rationale: rewrite.rationale,
                  highlights: rewrite.highlights,
                  createdAt: timestamp,
                });

                return {
                  ...section,
                  value: rewrite.content,
                  draftVersions,
                  analysis: null,
                };
              }),
              polishChecklist: Array.from(
                new Set([
                  ...project.profileWorkspace.polishChecklist,
                  ...rewrite.polishChecklist,
                ])
              ),
              audit: null,
              updatedAt: timestamp,
            },
            updatedAt: timestamp,
          }),
          get().outcomeLogs,
          get().retestLogs
        );

        set({ projects: nextProjects });
        const token = getAuthToken();
        if (token) debouncedSave(token);
      },

      applyProjectProfileAnalysis: (projectId, sectionId, analysis) => {
        const timestamp = new Date().toISOString();
        const nextProjects = updateProjectCollection(
          get().projects,
          projectId,
          project => ({
            ...project,
            profileWorkspace: {
              ...project.profileWorkspace,
              sections: project.profileWorkspace.sections.map(section =>
                section.id === sectionId
                  ? {
                      ...section,
                      analysis: {
                        ...analysis,
                        analyzedAt: analysis.analyzedAt || timestamp,
                      },
                    }
                  : section
              ),
              audit: null,
              updatedAt: timestamp,
            },
            updatedAt: timestamp,
          }),
          get().outcomeLogs,
          get().retestLogs
        );

        set({ projects: nextProjects });
        const token = getAuthToken();
        if (token) debouncedSave(token);
      },

      applyProjectAudit: (projectId, audit) => {
        const timestamp = new Date().toISOString();
        const nextProjects = updateProjectCollection(
          get().projects,
          projectId,
          project => ({
            ...project,
            profileWorkspace: {
              ...project.profileWorkspace,
              audit,
              updatedAt: timestamp,
            },
            updatedAt: timestamp,
          }),
          get().outcomeLogs,
          get().retestLogs
        );

        set({ projects: nextProjects });
        const token = getAuthToken();
        if (token) debouncedSave(token);
      },

      restoreProjectProfileVersion: (projectId, sectionId, versionId) => {
        const timestamp = new Date().toISOString();
        const nextProjects = updateProjectCollection(
          get().projects,
          projectId,
          project => ({
            ...project,
            profileWorkspace: {
              ...project.profileWorkspace,
              sections: project.profileWorkspace.sections.map(section => {
                if (section.id !== sectionId) return section;
                const version = section.draftVersions.find(item => item.id === versionId);
                if (!version) return section;

                let draftVersions = section.draftVersions;
                if (isFilled(section.value) && section.value !== version.content) {
                  draftVersions = appendDraftVersion(draftVersions, {
                    id: `${projectId}-${sectionId}-restore-manual-${timestamp}`,
                    content: section.value,
                    source: 'manual',
                    label: 'Manual draft',
                    tone: null,
                    rationale: null,
                    highlights: [],
                    createdAt: timestamp,
                  });
                }

                draftVersions = appendDraftVersion(draftVersions, {
                  id: `${projectId}-${sectionId}-restored-${timestamp}`,
                  content: version.content,
                  source: 'restored',
                  label: `Restored · ${version.label}`,
                  tone: version.tone,
                  rationale: version.rationale,
                  highlights: version.highlights,
                  createdAt: timestamp,
                });

                return {
                  ...section,
                  value: version.content,
                  draftVersions,
                  analysis: null,
                };
              }),
              audit: null,
              updatedAt: timestamp,
            },
            updatedAt: timestamp,
          }),
          get().outcomeLogs,
          get().retestLogs
        );

        set({ projects: nextProjects });
        const token = getAuthToken();
        if (token) debouncedSave(token);
      },

      recordPhotoAnalysis: ({ vertical, photoId, score, recordedAt }) => {
        const projectId = getProjectIdForVertical(vertical);
        const timestamp = recordedAt || new Date().toISOString();
        const nextProjects = updateProjectCollection(
          get().projects,
          projectId,
          project => {
            const photoIds = project.photoIds.includes(photoId)
              ? project.photoIds
              : [...project.photoIds, photoId];
            const bestScore = project.bestScore == null ? score : Math.max(project.bestScore, score);
            const bestPhotoId =
              project.bestScore == null || score >= project.bestScore ? photoId : project.bestPhotoId;

            return {
              ...project,
              profileWorkspace: {
                ...project.profileWorkspace,
                audit: null,
              },
              currentScore: score,
              bestScore,
              latestPhotoId: photoId,
              bestPhotoId,
              photoIds,
              updatedAt: timestamp,
            };
          },
          get().outcomeLogs,
          get().retestLogs
        );

        set({
          projects: nextProjects,
          activeProjectId: projectId,
        });
        const token = getAuthToken();
        if (token) saveImmediately(token);
      },

      recordCompareRun: (projectId, compareRun) => {
        const nextProjects = updateProjectCollection(
          get().projects,
          projectId,
          project => {
            const sortedRankings = [...compareRun.rankings].sort((a, b) => b.score - a.score);
            const winner = sortedRankings[0];
            const existingRuns = project.compareRuns.filter(run => run.id !== compareRun.id);
            const photoIds = Array.from(
              new Set([
                ...project.photoIds,
                ...sortedRankings.map(ranking => ranking.photoId),
              ])
            );
            const bestScore =
              winner && project.bestScore != null
                ? Math.max(project.bestScore, winner.score)
                : winner?.score ?? project.bestScore;
            const bestPhotoId =
              winner && (project.bestScore == null || winner.score >= project.bestScore)
                ? winner.photoId
                : project.bestPhotoId;

            return {
              ...project,
              profileWorkspace: {
                ...project.profileWorkspace,
                audit: null,
              },
              currentScore: winner?.score ?? project.currentScore,
              bestScore: bestScore ?? null,
              latestPhotoId: winner?.photoId ?? project.latestPhotoId,
              bestPhotoId: bestPhotoId ?? null,
              photoIds,
              compareRuns: [...existingRuns, { ...compareRun, rankings: sortedRankings }],
              updatedAt: compareRun.createdAt,
            };
          },
          get().outcomeLogs,
          get().retestLogs
        );

        set({ projects: nextProjects });
        const token = getAuthToken();
        if (token) saveImmediately(token);
      },

      recordRetest: ({ vertical, photoId, score, recordedAt }) => {
        const projectId = getProjectIdForVertical(vertical);
        const timestamp = recordedAt || new Date().toISOString();
        const project =
          mergeProjectsWithDefaults(get().projects).find(item => item.id === projectId) ||
          createDefaultProjects().find(item => item.id === projectId)!;
        const previousScore = project.currentScore;

        const existingRetest = get().retestLogs.find(
          log => log.projectId === projectId && log.photoId === photoId && log.score === score
        );

        const nextRetestLogs = existingRetest
          ? get().retestLogs
          : [
              ...get().retestLogs,
              {
                id: `${projectId}-${photoId}-${timestamp}`,
                projectId,
                photoId,
                previousScore,
                score,
                change: previousScore == null ? null : score - previousScore,
                loggedAt: timestamp,
              },
            ];

        const nextProjects = updateProjectCollection(
          get().projects,
          projectId,
          currentProject => {
            const photoIds = currentProject.photoIds.includes(photoId)
              ? currentProject.photoIds
              : [...currentProject.photoIds, photoId];
            const bestScore = currentProject.bestScore == null ? score : Math.max(currentProject.bestScore, score);
            const bestPhotoId =
              currentProject.bestScore == null || score >= currentProject.bestScore
                ? photoId
                : currentProject.bestPhotoId;

            return {
              ...currentProject,
              profileWorkspace: {
                ...currentProject.profileWorkspace,
                audit: null,
              },
              currentScore: score,
              bestScore,
              latestPhotoId: photoId,
              bestPhotoId,
              photoIds,
              updatedAt: timestamp,
            };
          },
          get().outcomeLogs,
          nextRetestLogs
        );

        set({
          projects: nextProjects,
          activeProjectId: projectId,
          retestLogs: nextRetestLogs,
        });
        const token = getAuthToken();
        if (token) saveImmediately(token);
      },

      logOutcome: (projectId: string, metric: OutcomeMetric, value: number, note?: string) => {
        const timestamp = new Date().toISOString();
        const nextOutcomeLogs = [
          ...get().outcomeLogs,
          {
            id: `${projectId}-${metric}-${timestamp}`,
            projectId,
            metric,
            value,
            note,
            loggedAt: timestamp,
          },
        ];
        const nextProjects = mergeProjectsWithDefaults(get().projects).map(project =>
          project.id === projectId
            ? syncProjectState({ ...project, updatedAt: timestamp }, nextOutcomeLogs, get().retestLogs)
            : syncProjectState(project, nextOutcomeLogs, get().retestLogs)
        );

        set({ outcomeLogs: nextOutcomeLogs, projects: nextProjects });
        const token = getAuthToken();
        if (token) debouncedSave(token);
      },

      // Load game state from server — called on auth state change
      loadFromServer: async (token: string) => {
        try {
          set({ _syncing: true });
          const res = await fetch(`${API_CONFIG.RATE_URL}/user/progress`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) {
            set({ _syncing: false });
            return;
          }
          const data = await res.json() as Record<string, unknown>;
          set({
            activeContexts: (data.activeContexts as string[]) || [],
            primaryContext: (data.primaryContext as string) || '',
            xp: (data.xp as number) || 0,
            level: getLevelFromXP((data.xp as number) || 0),
            streakDays: (data.streakDays as number) || 0,
            lastStreakDate: (data.lastStreakDate as string) || null,
            achievements: (data.achievements as string[]) || [],
            onboardingComplete: (data.onboardingComplete as boolean) || false,
            completedLessons: (data.completedLessons as string[]) || [],
            weeklyResults: (data.weeklyResults as WeeklyResult[]) || [],
            projects: mergeProjectsWithDefaults((data.projects as ProfileProject[]) || []),
            activeProjectId:
              (data.activeProjectId as string) || getProjectIdForVertical('dating'),
            outcomeLogs: (data.outcomeLogs as OutcomeLog[]) || [],
            retestLogs: (data.retestLogs as RetestLog[]) || [],
            _syncing: false,
            _lastSyncedAt: new Date().toISOString(),
          });
        } catch {
          set({ _syncing: false });
          // Fall back to localStorage state (persist middleware handles this)
        }
      },

      // Save current state to server
      saveToServer: async (token: string) => {
        const state = get();
        try {
          await fetch(`${API_CONFIG.RATE_URL}/user/progress`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              activeContexts: state.activeContexts,
              primaryContext: state.primaryContext,
              xp: state.xp,
              level: state.level,
              streakDays: state.streakDays,
              lastStreakDate: state.lastStreakDate,
              achievements: state.achievements,
              onboardingComplete: state.onboardingComplete,
              completedLessons: state.completedLessons,
              weeklyResults: state.weeklyResults,
              projects: state.projects,
              activeProjectId: state.activeProjectId,
              outcomeLogs: state.outcomeLogs,
              retestLogs: state.retestLogs,
            }),
          });
        } catch {
          // Silent fail — localStorage still has the state
        }
      },

      resetState: () => set(DEFAULT_STATE),
    }),
    {
      name: 'photofeeler-game',
      // localStorage serves as offline cache — server is source of truth
    }
  )
);
